"use client";

import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useMemo } from "react";
import { InventoryMovementTypeSchema } from "@/modules/merchant/domain/contracts";
import {
  type ShopBranchOption,
  type ShopSellableCatalogRow,
  shopCatalogApi,
  shopInventoryApi,
} from "@/modules/merchant/presentation/api/client";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

const INVENTORY_MOVEMENTS_ROUTE =
  "/app/shops/$shopId/inventory/movements" as const;

/** Radix Select ບໍ່ຮອງຮັບ value ວ່າງ — ໃຊ້ sentinel ແທນ "ບໍ່ກອງປະເພດ" */
const MOVEMENT_TYPE_FILTER_ALL = "__movement_type_all__";

export const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  receive: "ຮັບເຂົ້າ",
  sale: "ຂາຍ",
  refund: "ຄືນ",
  adjust: "ປັບ",
  transfer_out: "ໂອນອອກ",
  transfer_in: "ໂອນເຂົ້າ",
  reserve: "ຈອງ",
  release: "ປົດຈອງ",
};

export function movementTypeBadge(type: string) {
  const label = MOVEMENT_TYPE_LABELS[type] ?? type;
  switch (type) {
    case "receive":
    case "transfer_in":
      return (
        <Badge
          variant="default"
          className="border-transparent bg-emerald-600 font-normal hover:bg-emerald-600"
        >
          {label}
        </Badge>
      );
    case "refund":
    case "release":
      return (
        <Badge
          variant="default"
          className="border-transparent bg-sky-600 font-normal text-white hover:bg-sky-600"
        >
          {label}
        </Badge>
      );
    case "sale":
    case "transfer_out":
      return (
        <Badge variant="destructive" className="font-normal">
          {label}
        </Badge>
      );
    case "reserve":
      return (
        <Badge
          variant="secondary"
          className="border border-amber-500/40 bg-amber-500/10 font-normal text-amber-900 dark:text-amber-200"
        >
          {label}
        </Badge>
      );
    case "adjust":
      return (
        <Badge variant="secondary" className="font-normal">
          {label}
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="font-normal text-xs">
          {label}
        </Badge>
      );
  }
}

export function ShopInventoryMovementsFilter() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate({ from: INVENTORY_MOVEMENTS_ROUTE });
  const search = useSearch({ from: INVENTORY_MOVEMENTS_ROUTE });

  const branchQueryKey = useMemo(
    () => ["shops", shopId ?? "", "inventory-movements", "branch"] as const,
    [shopId],
  );
  const skuQueryKey = useMemo(
    () => ["shops", shopId ?? "", "inventory-movements", "sku"] as const,
    [shopId],
  );

  const hasFilter =
    !!search.branchId ||
    !!search.shopProductSkuId ||
    !!search.type ||
    !!search.createdFrom ||
    !!search.createdTo;

  if (!shopId) return null;

  const dateInputClass =
    "h-8 w-full sm:w-[10.5rem] bg-background text-sm [color-scheme:light] dark:[color-scheme:dark]";

  return (
    <div className="mb-2 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <InfiniteCombobox<ShopBranchOption>
            queryKey={[...branchQueryKey, "filter"]}
            queryFn={async ({ search: q, pageParam }) =>
              shopInventoryApi.lookupBranches(shopId, {
                page: pageParam,
                search: q.trim() || undefined,
                limit: 20,
              })
            }
            preloadQueryFn={(id) => shopInventoryApi.getBranch(shopId, id)}
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={search.branchId ?? ""}
            onValueChange={(id) => {
              nav({
                to: INVENTORY_MOVEMENTS_ROUTE,
                params: { shopId },
                search: {
                  ...search,
                  offset: 0,
                  branchId: id || undefined,
                },
              });
            }}
            placeholder="ສາຂາ"
            className="h-8 w-full sm:w-52"
            clearable
          />
          <InfiniteCombobox<ShopSellableCatalogRow>
            queryKey={[...skuQueryKey, "filter"]}
            queryFn={async ({ search: q, pageParam }) => {
              const limit = 20;
              const page = pageParam ?? 1;
              const offset = (page - 1) * limit;
              const res = await shopCatalogApi.listSellable(shopId, {
                offset,
                limit,
                q: q.trim() || undefined,
                barcode: undefined,
              });
              const next =
                offset + res.data.length < res.meta.total ? page + 1 : null;
              return { items: res.data, nextPage: next };
            }}
            preloadQueryFn={(id) => shopCatalogApi.getSellableSku(shopId, id)}
            getLabel={(item) => `${item.productName} · ${item.shopSku}`}
            getValue={(item) => item.id}
            value={search.shopProductSkuId ?? ""}
            onValueChange={(id) => {
              nav({
                to: INVENTORY_MOVEMENTS_ROUTE,
                params: { shopId },
                search: {
                  ...search,
                  offset: 0,
                  shopProductSkuId: id || undefined,
                },
              });
            }}
            placeholder="ສິນຄ້າ / SKU"
            className="h-8 w-full min-w-48 sm:max-w-md sm:flex-1"
            clearable
          />
          <Select
            value={search.type ?? MOVEMENT_TYPE_FILTER_ALL}
            onValueChange={(val) => {
              nav({
                to: INVENTORY_MOVEMENTS_ROUTE,
                params: { shopId },
                search: {
                  ...search,
                  offset: 0,
                  type: val === MOVEMENT_TYPE_FILTER_ALL ? undefined : val,
                },
              });
            }}
          >
            <SelectTrigger
              className="h-8 w-full sm:w-44"
              aria-label="ປະເພດ movement"
            >
              <SelectValue placeholder="ປະເພດທັງໝົດ" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={MOVEMENT_TYPE_FILTER_ALL}>
                ປະເພດທັງໝົດ
              </SelectItem>
              {InventoryMovementTypeSchema.options.map((t) => (
                <SelectItem key={t} value={t}>
                  {MOVEMENT_TYPE_LABELS[t] ?? t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            className={dateInputClass}
            aria-label="ຕັ້ງແຕ່ວັນທີ"
            value={search.createdFrom ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              nav({
                to: INVENTORY_MOVEMENTS_ROUTE,
                params: { shopId },
                search: {
                  ...search,
                  offset: 0,
                  createdFrom: v === "" ? undefined : v,
                },
              });
            }}
          />
          <Input
            type="date"
            className={dateInputClass}
            aria-label="ຫາວັນທີ"
            value={search.createdTo ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              nav({
                to: INVENTORY_MOVEMENTS_ROUTE,
                params: { shopId },
                search: {
                  ...search,
                  offset: 0,
                  createdTo: v === "" ? undefined : v,
                },
              });
            }}
          />
        </div>
        {hasFilter ? (
          <Button
            variant="ghost"
            size="sm"
            type="button"
            className="max-sm:w-full"
            onClick={() =>
              nav({
                to: INVENTORY_MOVEMENTS_ROUTE,
                params: { shopId },
                search: {
                  offset: 0,
                  limit: search.limit,
                  branchId: undefined,
                  shopProductSkuId: undefined,
                  type: undefined,
                  createdFrom: undefined,
                  createdTo: undefined,
                  recOnlyMismatch: search.recOnlyMismatch,
                },
              })
            }
          >
            <XIcon className="h-4 w-4" aria-hidden />
            ລ້າງ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
